import PropTypes from "prop-types";
import React from "react";
import {
  Row,
  Col,
  Alert,
  Card,
  CardBody,
  Container,
  FormFeedback,
  Input,
  Label,
  Form,
  Button,
  Spinner,
} from "reactstrap";
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { withRouter, Link } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";

import { userForgetPassword } from "../../store/actions";
import logoLight from "../../assets/images/logo-light.png";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";

const ForgetPasswordPage = (props) => {
  const dispatch = useDispatch();
  const { loading } = useSelector((state) => state.ForgetPassword);
  const { t } = useTranslation();

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: "",
    },
    validationSchema: Yup.object({
      email: Yup.string()
        .email(t("Validations.Email.Invalid"))
        .required(t("Validations.Email.Required")),
    }),
    onSubmit: (values) => {
      dispatch(userForgetPassword(values, props.history));
    },
  });

  const { forgetError, forgetSuccessMsg } = useSelector((state) => ({
    forgetError: state.ForgetPassword.forgetError,
    forgetSuccessMsg: state.ForgetPassword.forgetSuccessMsg,
  }));

  document.title = `${t("Reset Password")}  | ${t(
    process.env.REACT_APP_HEADING
  )}`;

  return (
    <ParticlesAuth>
      <div className="auth-page-content">
        <Container>
          <Row>
            <Col lg={12}>
              <div className="text-center mt-sm-5 mb-4 text-white-50">
                <div>
                  <Link to="/" className="d-inline-block auth-logo">
                    <img src={logoLight} alt="" height="20" />
                  </Link>
                </div>
                <p className="mt-3 fs-15 fw-medium">
                  {t("Common.Technical Services at your door step")}
                </p>
              </div>
            </Col>
          </Row>

          <Row className="justify-content-center">
            <Col md={8} lg={6} xl={5}>
              <Card className="mt-4">
                <CardBody className="p-4">
                  <div className="text-center mt-2">
                    <h5 className="text-primary">
                      {t("ForgotPassword.Forgot Password?")}
                    </h5>
                    <p className="text-muted">
                      {t("ForgotPassword.Reset password heading", {
                        APP_NAME: process.env.REACT_APP_NAME,
                      })}
                    </p>

                    <lord-icon
                      src="https://cdn.lordicon.com/rhvddzym.json"
                      trigger="loop"
                      colors="primary:#0ab39c"
                      className="avatar-xl"
                      style={{ width: "120px", height: "120px" }}
                    ></lord-icon>
                  </div>

                  <Alert
                    className="alert-borderless alert-warning text-center mb-2 mx-2"
                    role="alert"
                  >
                    {t(
                      "ForgotPassword.Enter your email and instructions will be sent to you"
                    )}
                  </Alert>
                  <div className="p-2">
                    {forgetError && forgetError ? (
                      <Alert color="danger" style={{ marginTop: "13px" }}>
                        {t(`Messages.Error.${forgetError}`)}{" "}
                      </Alert>
                    ) : null}
                    {forgetSuccessMsg ? (
                      <Alert color="success" style={{ marginTop: "13px" }}>
                        {t(`Messages.Success.${forgetSuccessMsg}`)}
                      </Alert>
                    ) : null}
                    <Form
                      onSubmit={(e) => {
                        e.preventDefault();
                        validation.handleSubmit();
                        return false;
                      }}
                    >
                      <div className="mb-4">
                        <Label className="form-label">
                          {t("Fields.Email")}
                        </Label>
                        <Input
                          name="email"
                          className="form-control"
                          placeholder={t("Fields.Enter Email")}
                          type="email"
                          onChange={validation.handleChange}
                          onBlur={validation.handleBlur}
                          value={validation.values.email || ""}
                          invalid={
                            validation.touched.email && validation.errors.email
                              ? true
                              : false
                          }
                        />
                        {validation.touched.email && validation.errors.email ? (
                          <FormFeedback type="invalid">
                            <div>{validation.errors.email}</div>
                          </FormFeedback>
                        ) : null}
                      </div>
                      <div className="mt-4">
                        <Button
                          color="success"
                          className="btn-load w-100"
                          disabled={loading}
                        >
                          {loading ? (
                            <span className="d-flex align-items-center">
                              <span className="flex-grow-1 me-2">
                                {t("ForgotPassword.Sending")}...
                              </span>
                              <Spinner
                                size="sm"
                                className="flex-shrink-0"
                                role="status"
                              >
                                {" "}
                                {t("ForgotPassword.Sending")}...{" "}
                              </Spinner>
                            </span>
                          ) : (
                            <> {t("ForgotPassword.Send Reset Link")}</>
                          )}
                        </Button>
                      </div>
                    </Form>
                  </div>
                </CardBody>
              </Card>

              <div className="mt-4 text-center">
                <p className="mb-0">
                  {t("ForgotPassword.Wait, I remember my password")}...{" "}
                  <Link
                    to="/login"
                    className="fw-semibold text-primary text-decoration-underline"
                  >
                    {" "}
                    {t("Common.Click here")}{" "}
                  </Link>{" "}
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    </ParticlesAuth>
  );
};

ForgetPasswordPage.propTypes = {
  history: PropTypes.object,
};

export default withRouter(ForgetPasswordPage);
